import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Play, Lock, Sparkles, Zap } from 'lucide-react';
import { PEOPLE } from '../constants';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pt-32 pb-48 px-6 md:px-12 bg-black">
      <header className="mb-24 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ 
            type: "spring", 
            stiffness: 260, 
            damping: 20,
            delay: 0.1 
          }}
          className="relative inline-flex items-center gap-4 px-10 py-4 mb-10 overflow-hidden rounded-2xl group cursor-default"
        >
          {/* Scanning Light Beam */}
          <motion.div
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear", repeatDelay: 1 }}
            className="absolute top-0 left-0 w-20 h-full bg-white/20 skew-x-[25deg] blur-xl z-20 pointer-events-none"
          />

          {/* Animated Background Gradient */}
          <motion.div 
            animate={{ 
              backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
            }}
            transition={{ 
              duration: 5, 
              repeat: Infinity, 
              ease: "linear" 
            }}
            className="absolute inset-0 bg-gradient-to-r from-green-600 via-emerald-400 to-green-900 bg-[length:200%_auto] opacity-30 blur-sm"
          />
          
          <div className="absolute inset-0 bg-zinc-950/90 border border-white/10 rounded-2xl group-hover:border-green-500/50 group-hover:bg-zinc-900 transition-all duration-500 shadow-[0_0_20px_rgba(34,197,94,0.1)]" />
          
          <div className="relative flex items-center gap-4 z-10">
            {/* Rotating Kickass Badge */}
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-500/20 border border-green-500/40 text-green-400"
            >
              < Zap size={14} fill="currentColor" />
            </motion.div>

            <div className="flex flex-col items-start leading-none text-left">
              <span className="text-[9px] font-black tracking-[0.5em] text-white/40 uppercase mb-1">Official Selection</span>
              <span className="text-xs md:text-sm font-black tracking-[0.3em] text-white uppercase italic">
                Presented by <span className="text-green-400 drop-shadow-[0_0_8px_rgba(34,197,94,0.5)]">Skinny Will</span> & <span className="text-green-400 drop-shadow-[0_0_8px_rgba(34,197,94,0.5)]">Skinny D</span>
              </span>
            </div>

            <Sparkles size={14} className="text-green-400 animate-pulse hidden md:block" />
          </div>

          {/* Glitch Overlay on Hover */}
          <motion.div 
            className="absolute inset-0 bg-green-500/10 mix-blend-overlay opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            animate={ {
              x: [0, -2, 2, 0],
              y: [0, 1, -1, 0]
            }}
            transition={{ repeat: Infinity, duration: 0.1 }}
          />

          {/* Neon Glow beneath */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-green-500/40 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </motion.div>
        
        <motion.h1 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative text-[10vw] md:text-[8rem] font-black font-montserrat text-white tracking-tighter leading-[0.8] uppercase flex flex-col items-center"
        >
          <span>BEACHHOUSE</span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-blue-500 to-purple-500">WRAPPED</span>
          
          <motion.span 
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 100 }}
            className="block text-[18vw] md:text-[20rem] mt-[-3vw] leading-none font-black italic tracking-tighter select-none pointer-events-none"
            style={{
              WebkitTextStroke: '3px rgba(34, 197, 94, 0.6)',
              textShadow: '0 0 100px rgba(34, 197, 94, 0.5), 0 0 30px rgba(34, 197, 94, 0.7)',
              color: 'transparent',
              backgroundImage: 'linear-gradient(to bottom, #22c55e, #166534)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text'
            }}
          >
            2025
          </motion.span>
        </motion.h1>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto">
        {PEOPLE.map((person, index) => {
          const isLive = !!person.videoUrl;
          
          return (
            <motion.div
              key={person.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(`/video/${person.id}`)}
              className={`aspect-[3/4] relative rounded-3xl overflow-hidden cursor-pointer group bg-zinc-900 border ${isLive ? 'border-white/10' : 'border-white/5 opacity-40'}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${person.color} opacity-40 group-hover:opacity-60 transition-opacity duration-500`} />
              <div className="absolute inset-0 p-8 flex flex-col justify-end bg-gradient-to-t from-black via-black/20 to-transparent">
                <h3 className="text-3xl font-black text-white mb-2 tracking-tighter italic">
                  {person.name}
                </h3>
                {isLive ? (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white text-black flex items-center justify-center">
                      <Play size={12} fill="currentColor" />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white/80">Play Reel</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-white/30">
                    <Lock size={12} />
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Hidden</span>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default HomePage;